import { CopyIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { DAYS_OF_WEEK_ORDERED } from "@/lib/constants";
import { DayOfWeek } from "@/types";

export const CopyDayRangesPopover = ({
  sourceDay,
  onApply,
}: {
  sourceDay: DayOfWeek;
  onApply: (targetDays: DayOfWeek[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<DayOfWeek[]>([]);

  useEffect(() => {
    if (!open) setSelected([]);
  }, [open]);

  const others = DAYS_OF_WEEK_ORDERED.filter((d) => d !== sourceDay);

  const toggle = (day: DayOfWeek, checked: boolean) => {
    setSelected((prev) =>
      checked ? [...prev, day] : prev.filter((d) => d !== day),
    );
  };

  const render = () => (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-slate-500 hover:bg-transparent hover:text-slate-900"
        >
          <CopyIcon className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-3">
        <Column className="gap-2.5">
          <Text className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Copy times to
          </Text>
          <Column className="gap-1">
            {others.map((d) => (
              <Row
                key={d}
                className="cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                onClick={() => {
                  toggle(d, !selected.includes(d));
                }}
              >
                <Checkbox
                  checked={selected.includes(d)}
                  onCheckedChange={(next) => {
                    toggle(d, next === true);
                  }}
                />
                <Text className="text-sm text-slate-700">{d}</Text>
              </Row>
            ))}
          </Column>
          <Button
            size="sm"
            disabled={selected.length === 0}
            onClick={() => {
              onApply(selected);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </Column>
      </PopoverContent>
    </Popover>
  );

  return render();
};
